import { useEffect, useState } from "react";
import { Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { SellerWebhooks } from "@/models";
import { showToast } from "@/utils";
import { useSettings, useIntegration } from "@/hooks";
import { Input, FormModal, ConfirmationModal } from "@/components";
import { IntegrationEmptyState } from "../_EmptyState";
import { WebhookItem } from "./_webhook-item";
import { ChargeDomainField } from "./_charge-domain-field";
import { buildWebhookDomains } from "./_webhook-domains";
import { Button } from "barte-design-system";
import { Alert, Checkbox, Select } from "barte-design-system";

const webhookVersionOptions = [
  { label: "v1", value: "1.0" },
  { label: "v2", value: "2.0" },
];

const webhookCreateScheama = yup.object().shape({
  title: yup.string().required("Preenchimento obrigatório!"),
  url: yup
    .string()
    .required("Preenchimento obrigatório!")
    .matches(
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
      "Entre com uma url correta"
    )
    .required("Preenchimento obrigatório!"),
  version: yup.string(),
  subscriptions: yup.boolean(),
  order: yup.boolean(),
  charge: yup.boolean(),
});

const webhookDefaultValues = {
  title: "",
  url: "",
  version: "1.0",
  subscriptions: false,
  order: false,
  charge: false,
};

export const WebHooks = () => {
  const {
    webhooks,
    getWebhooksInitialData,
    isLoading,
    setIsLoading,
    isWebhookDeleteModalOpen,
    isWebhookAlertActiveOpen,
    setIsWebhookAlertActiveOpen,
    setIsWebhookDeleteModalOpen,
    changeWebhookActive,
    chooseWebhook,
    deleteWebhook,
  } = useIntegration();

  const { postSellerApiWebhook } = useSettings();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useEffect(() => {
    getWebhooksInitialData();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(webhookCreateScheama),
    defaultValues: webhookDefaultValues,
  });

  useEffect(() => {
    if (!isCreateModalOpen) {
      reset(webhookDefaultValues);
    }
  }, [isCreateModalOpen, reset]);

  const getTitleValue = watch("title");
  const getUrlValue = watch("url");
  const getVersionValue = watch("version");
  const getSubscriptionsValue = watch("subscriptions");
  const getOrderValue = watch("order");
  const getChargeValue = watch("charge");
  const selectedDomains = buildWebhookDomains({
    subscriptions: getSubscriptionsValue,
    order: getOrderValue,
    charge: getChargeValue,
    version: getVersionValue,
  });



  const onSubmit = handleSubmit(
    async (
      data: Pick<
        SellerWebhooks,
        "title" | "url" | "version" | "subscriptions" | "order" | "charge"
      >
    ) => {
      try {
        const domains = buildWebhookDomains(data);
        const formData = {
          title: data.title,
          domains,
          url: data.url,
          version: data.version,
        };
        setIsLoading(true);
        await postSellerApiWebhook(formData);
        getWebhooksInitialData();
        showToast({ message: "Webhook criado com sucesso!", type: "success" });
      } catch (error) {
        error?.response?.data?.errors.map((error) => {
          showToast({
            type: "error",
            message: `${error?.code} - ${error?.description}`
          })
        });
      } finally {
        setIsLoading(false);
        setIsCreateModalOpen(!isCreateModalOpen);
      }
    }
  );
  return (
    <>
      <ConfirmationModal
        isOpen={isWebhookDeleteModalOpen}
        onClose={() => setIsWebhookDeleteModalOpen(!isWebhookDeleteModalOpen)}
        handleConfirmModal={deleteWebhook}
        title="Tem certeza que deseja excluir esse webhook?"
        isSubmitting={isLoading}
        yesNoText
      >
        <Text>Essa ação não poderá ser desfeita.</Text>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={isWebhookAlertActiveOpen}
        onClose={() => setIsWebhookAlertActiveOpen(!isWebhookAlertActiveOpen)}
        handleConfirmModal={changeWebhookActive}
        isSubmitting={isLoading}
        title={
          chooseWebhook.active
            ? "Tem certeza que deseja desativar esse webhook?"
            : "Tem certeza que deseja reativar esse webhook?"
        }
        yesNoText
      >
        {chooseWebhook.active ? (
          <Text>
            As integrações relativas a esse webhook irão parar de funcionar
            imediatamente.
          </Text>
        ) : (
          <Text>
            As integrações relativas a esse webhook começarão a funcionar
            imediatamente.
          </Text>
        )}
      </ConfirmationModal>
      <FormModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(!isCreateModalOpen);
        }}
        title="Novo Webhook"
        handleConfirmModal={onSubmit}
        isSubmitting={isLoading}
        disableWhenIsFormEmpty={
          !getTitleValue || !getUrlValue || selectedDomains.length === 0
        }
        confirmationButtonText="Salvar"
      >
        <Stack spacing={4}>
          <Alert
            title="Atenção à versão dos eventos"
            variant="warning"
            description="A v1 será descontinuada para os eventos de Cobrança Pontual. Ao criar o webhook em v2, apenas os eventos de Cobrança Pontual passam a ser enviados no formato v2 — os eventos de Assinaturas permanecem em v1 por enquanto (uma v2 para Assinaturas será disponibilizada futuramente)."
            showCloseButton={false}
          />
          <Input
            type="text"
            label="Nome"
            name="title"
            register={{ ...register("title") }}
            borderRadius="2px"
            errors={errors?.title}
          />
          <Input
            type="text"
            label="Sua URL"
            name="url"
            register={{ ...register("url") }}
            borderRadius="2px"
            errors={errors?.url}
          />
          <Select
            label="Versão"
            options={webhookVersionOptions}
            value={getVersionValue ?? "1.0"}
            onChange={(value) => {
              setValue("version", value, { shouldValidate: true });
              if (value === "1.0") {
                setValue("charge", false);
              }
            }}
          />
          <Text color="gray.400" fontSize=".8rem">
            Domínios
          </Text>
          <Checkbox
            name="subscriptions"
            {...register("subscriptions")}
            label="Assinaturas"
            onChange={(checked) => {
              console.log(checked);
            }}
          />
          <Checkbox
            name="orders"
            {...register("order")}
            label="Cobrança Pontual"
            onChange={(checked) => {
              console.log(checked);
            }}
          />
          <ChargeDomainField
            isVersion2={getVersionValue === "2.0"}
            isChecked={!!getChargeValue}
            onChange={(checked) => setValue("charge", checked)}
          />


        </Stack>
      </FormModal>
      <Flex w="100%" direction="column" gap={4}>
        <Flex align="center">
          <Heading fontSize="20px">Webhooks</Heading>
          <Flex gap={8} ml="auto">
            <Button
              variant="outline"
              onClick={() => setIsCreateModalOpen(!isCreateModalOpen)}
            >
              Novo Webhook
            </Button>
          </Flex>
        </Flex>
        {webhooks.length > 0 ? (
          <Stack>
            {webhooks?.map((webhook) => (
              <WebhookItem key={webhook.uuid} webhook={webhook} />
            ))}
          </Stack>
        ) : (
          <IntegrationEmptyState message="Nenhum webhook cadastrado ainda" />
        )}
      </Flex>
    </>
  );
};
